/**
 * Утилиты для форматирования значений (единый источник правды)
 */

export type FormatPriceOptions = {
  /** Локаль для числа (по умолчанию ru-RU) */
  locale?: string;
  /** Суффикс валюты; true = " ₽", false = без суффикса (только число) */
  suffix?: boolean;
  /** Максимум знаков после запятой (0 = целые) */
  maximumFractionDigits?: number;
};

/**
 * Форматирует числовое значение как цену в рублях
 * @param value - числовое значение или строка
 * @param options - locale, suffix (по умолчанию true), maximumFractionDigits (по умолчанию 0)
 * @returns строка вида "1 500 ₽" или "1 500" при suffix: false
 */
export function formatPrice(
  value: number | string | null | undefined,
  options: FormatPriceOptions = {}
): string {
  const { locale = 'ru-RU', suffix = true, maximumFractionDigits = 0 } = options;
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  const formatted = num.toLocaleString(locale, { maximumFractionDigits });
  return suffix ? `${formatted} ₽` : formatted;
}

/** Суффикс количества товара: " ×6" при qty>1, "" при 1. Единый формат везде (карточки, статистика). */
export function formatProductQuantitySuffix(quantity: number | null | undefined): string {
  const q = quantity ?? 1;
  return q > 1 ? ` ×${q}` : '';
}

export type FormatDurationLocale =
  | 'ru'
  | 'en'
  | 'uz'
  | 'tg'
  | 'ky'
  | 'az'
  | 'hy'
  | 'kk'
  | 'hi'
  | 'it'
  | 'ro';

/** Компактный формат для web-master schedule: «1ч 30м». */
export function formatDurationCompact(minutes?: number | null): string {
  if (minutes === undefined || minutes === null) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}ч ${mins}м`;
  if (hours > 0) return `${hours}ч`;
  return `${mins}м`;
}

/** Локализованный формат длительности (web-booking и др.). */
export function formatDurationLocalized(
  minutes?: number | null,
  locale: FormatDurationLocale = 'ru'
): string {
  if (minutes === undefined || minutes === null) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const langUnits: Record<FormatDurationLocale, { hour: string; minute: string }> = {
    ru: { hour: 'ч', minute: 'мин' },
    en: { hour: 'h', minute: 'min' },
    uz: { hour: 'soat', minute: 'daq' },
    tg: { hour: 'соат', minute: 'дақ' },
    ky: { hour: 'саат', minute: 'мүн' },
    az: { hour: 'saat', minute: 'dəq' },
    hy: { hour: 'ժ', minute: 'ր' },
    kk: { hour: 'сағ', minute: 'мин' },
    hi: { hour: 'घं', minute: 'मि' },
    it: { hour: 'h', minute: 'min' },
    ro: { hour: 'h', minute: 'min' },
  };
  const units = langUnits[locale];
  if (hours > 0 && mins > 0) return `${hours} ${units.hour} ${mins} ${units.minute}`;
  if (hours > 0) return `${hours} ${units.hour}`;
  return `${mins} ${units.minute}`;
}
